import { NzSelectComponent } from 'ng-zorro-antd/select';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR} from '@angular/forms';
import { InputBoolean } from 'ng-zorro-antd/core/util';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AbstractControlComponent } from "../../../classes/abstract-control.component";
import {cloneDeep} from "lodash";
import {BehaviorSubject, Observable, of, Subject} from "rxjs";
import {debounceTime, switchMap, takeUntil} from "rxjs/operators";
import {OptionModel} from "../../../vss-ui.model";
import {Ultilities} from '../../../extensions/ultilities';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'vss-ui-select-box',
  templateUrl: './cs-select-scroll.component.html',
  styleUrls: ['./cs-select-scroll.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CsSelectScrollComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
})
export class CsSelectScrollComponent extends AbstractControlComponent<any | any[]> implements OnInit, OnChanges, ControlValueAccessor, OnDestroy, AfterViewInit {
  private _destroy$: Subject<void> = new Subject<void>()
  @Input() checknzSuffixIcon: boolean = false;
  @Input() isLoading = false;
  pageLoad = { page: 1, size: 20 };
  @Input() csDropdownRenderTpl!: TemplateRef<any>;
  @Input() isErrorSelect: boolean = false;
  @Input() controlValue: any;
  @Input() optionList: any[] = [];
  @Input() csMode: 'default' | 'multiple' | 'tags' = 'default';
  @Input() csAllowClear: boolean = true;
  @Input() csDropdownMatchSelectWidth: boolean = false
  @Input() csShowSearch: boolean = true;
  @Input() csShowId: boolean = false;
  @Input() csIsMultiLevel: boolean = false;
  @Input() typeReturnMultilevel: 'all' | 'child' = 'child';
  @Input() csMaxTagCount!: number;
  @Input() keySearch!: string;
  @Input() params: any;
  @Input() suffixLabelSelected: string = '';
  @Input() fieldId: string = 'id';
  @Input() fieldLabel: string = 'name';
  @Input() @InputBoolean() valueObject!: boolean;
  @Input() getOptionsApi!: ((params: OptionModel) => Promise<any[]>) | undefined;
  @Input() checkDisable = (option: OptionModel) => false;
  @Input() isResetControlOnChangeParams = true;
  @Input() isAllowChangeOnEmpty = false; // allow to emit csChange when value is empty array
  @Input() isNumberOnly = false;
  @Input() getOptionsDefaultApi!: ((valuesSelected: any[]) => Observable<any>) | undefined;
  @Output() onClose = new EventEmitter<boolean>()
  @Output() onOpen = new EventEmitter<boolean>();
  @Output() csOpenChange = new EventEmitter();
  @Output() onInputChange = new EventEmitter();
  @Output() onInputChangeFullValue = new EventEmitter();

  @ViewChild('nzSelectRef') nzSelectRef!: NzSelectComponent
  REGEX_TEXT = /[^a-z0-9 ]/gi
  isClose = false

  options: any[] = []
  selectedValues: any[] = []
  fullValueSelected: any;
  keywordBind: BehaviorSubject<string> = new BehaviorSubject<string>('')
  pagingForm!: FormGroup
  cancelClose = false
  keywordSearch = ''

  get selectedValuesAsync() {
    return this.selectedValues?.map((x: any) => this.options.find((o: any) => o.value == x.value) || x)
  }

  constructor(
    private message: NzMessageService,
    private fb: FormBuilder,
    public translate: TranslateService
    ) {
    super();
  }

  ngOnInit() {
    this.options = cloneDeep(this.optionList)
    this.loadMore();
    this.pagingForm = this.fb.group({
      page: this.fb.control(1),
      size: this.fb.control(20),
    })
    this.pagingForm.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        switchMap(paging => {
          return this.apiStreamSearch({
            ...(this.params || {}),
            keyword: this.keywordBind.value,
            ...paging,
          })
        })
      ).subscribe()
    this.keywordBind.pipe(
      takeUntil(this._destroy$),
      debounceTime(300)
    ).subscribe(() => this.pagingForm.patchValue({page: 1}))
    if(this.csIsMultiLevel) {
      this.loadItemActive()
    }
  }

  loadItemActive() {
    const findInChild = (c: any) => {
      if(c.value == this.controlValue) {
        return c
      } else if (c?.children?.length) {
        c.children.forEach((i: any) => {
          if(findInChild(i)) {
            c.expanded = true
          }
        })
      }
    }
    this.optionList?.forEach(op => {
      findInChild(op)
    })
  }

  ngOnDestroy() {
    this._destroy$.next()
    this._destroy$.complete()
    this._destroy$.unsubscribe()
  }

  ngAfterViewInit() {
    this.getDefaultOptions(this.controlValue)
  }

  getDefaultOptions(values: any) {
    if (!values || (Array.isArray(values) && values.length == 0)) { return; }
    if(this.getOptionsDefaultApi){
      this.getOptionsDefaultApi(Array.isArray(values) ? values : `${typeof values == 'string' || values ? values : ''}`?.split(',')).subscribe()
    }
  }

  apiStreamSearch = (params: any) : Observable<any[]> => {
    return of([])
  }

  async writeValue(obj: any) {
    super.writeValue(obj);
    if (!obj || (Array.isArray(obj) && obj.length == 0 && !this.isAllowChangeOnEmpty)) { return; }
    this.getDefaultOptions(obj)
    this.onModelChange(obj);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.params && !changes?.params.firstChange) {
      this.pageLoad.page = 1;
      if (this.isResetControlOnChangeParams) {
        this.writeValue(null)
        this.onModelChange(null);
        this.optionList = [];
        this.onSearch('')
      }
      this.loadMore();
    }
    if (changes?.optionList && !changes?.optionList?.firstChange) {
      this.onSearch('')
      if(this.csIsMultiLevel) {
        this.loadItemActive()
      }
    }
  }

  loadMorePage() {
    if (this.isLoading) { return; }
    this.loadMore()
    this.pagingForm?.patchValue({page: this.pagingForm.value.page + 1})
  }

  async loadMore(): Promise<void> {
    this.isLoading = true;
    this.params = {
      ...this.params,
      ...this.pageLoad
    }
    try {
      if(this.getOptionsApi != undefined) {
        const options = await this.getOptionsApi(this.params);
        if(this.pageLoad.page == 1) {
          this.options = Ultilities.distinctData(Ultilities.convertNzOption(options, this.fieldId, this.fieldLabel, this.csShowId), 'value');
          this.pageLoad.page++;
        } else if (options.length > 0) {
          this.options = Ultilities.distinctData(this.options.concat(Ultilities.convertNzOption(options, this.fieldId, this.fieldLabel, this.csShowId)), 'value');
          this.pageLoad.page++;
        }
      }
    } catch (error: any) {
      this.message.error(error?.error?.error);
    } finally {
      this.isLoading = false;
    }
    // if(!this.keywordSearch) {
    //   if(Array.isArray(this.controlValue)) {
    //     let value = null
    //     value = this.controlValue.filter(v => this.options.find(o => o.value == v))
    //     if(value.length != this.controlValue.length) {
    //       this.writeValue(value)
    //       this.onModelChange(value);
    //     }
    //   } else if(this.options.find(o => o.value != this.controlValue)) {
    //     this.writeValue(null)
    //     this.onModelChange(null);
    //   }
    // }
  }

  searchText(text: string, valueCompare: string): boolean {
    return Ultilities.removeVietnameseAccents(`${text ? this.translate.instant(text) : text}`.trim()).includes(Ultilities.removeVietnameseAccents(`${valueCompare ? this.translate.instant(valueCompare) : valueCompare}`.trim()))
  }

  onSearch(value: any) {
    this.keywordSearch = value
    if (this.isNumberOnly && !Ultilities.validateNunber(value)) {
      this.options = [];
      return;
    }
    const newValue = this.getOptionsApi ? Ultilities.replaceSpecial(value) : value
    if(!this.getOptionsApi) {
      const searchDeep = (op: any) => {
        if(this.searchText(op.label, newValue)) {
          return op
        }
        if(op.children?.length && op.children.find((c: any) => searchDeep(c))) {
          return {
            ...op,
            expanded: true,
            children: op.children.filter((c: any) => searchDeep(c)).map((c: any) => searchDeep(c))
          }
        }
        return null
      }
      this.options = this.optionList.filter(x => !!searchDeep(x)).map(x => searchDeep(x))
    } else {
      this.pageLoad.page = 1;
      if (this.keySearch) {
        this.params[this.keySearch] = newValue;
      }
      this.loadMore();
    }
    this.keywordBind.next(newValue)
  }

  onModelChange(value: any) {
    const listOptions: any[] = []
    const getChild = (o: any) => {
      listOptions.push(o)
      o?.children?.forEach((c: any) => getChild(c))
    }
    this.options?.forEach(o => getChild(o))
    if(this.csMode == 'multiple') {
      this.selectedValues = value?.map((x: any) => [...listOptions, ...this.selectedValues]?.find((o: any) => o.value == x) || {value: x, label: x})
    } else if(this.fullValueSelected) {
      this.fullValueSelected = [...listOptions, this.fullValueSelected]?.find((o: any) => o.value == value) || {value: value, label: value}
    } else {
      this.selectedValues = [...listOptions, ...this.selectedValues]?.filter((o: any) => o.value == value) || {value: value, label: value}
    }
    this.onChangeFn(value);
    let data: any = listOptions?.find(c => c.value == value) ? {
      ...listOptions?.find(c => c.value == value)
    } : null
    if (data) {
      data.options = data?.init?.find((x: any) => x[this.fieldId] === data.value)?.options;
      data.fullValue = data?.init?.find((x: any) => x[this.fieldId] === data.value);
      // delete data.init;
    }
    this.csChange.emit(data || value);
  }

  openChange(event: boolean) {
    if(this.cancelClose) {
      this.cancelClose = false
      this.nzSelectRef.setOpenState(true)
      return
    }
    if(event) {
      this.loadItemActive()
      this.isClose = false
      this.onOpen.emit(true)
    } else {
      this.isClose = true
      this.onClose.emit(true)
    }
    this.csOpenChange.emit(event);
  }

  inputChange(val: any) {
    const data = this.options.find(x => x.value === val) ? {
      ...this.options.find(x => x.value === val)
    } : null
      if (data) {
        data.options = data?.init?.find((x: any) => x[this.fieldId] === data.value)?.options;
        data.fullValue = data?.init?.find((x: any) => x[this.fieldId] === data.value);
      }
      if(this.csMode == 'multiple') {
        this.onInputChangeFullValue.emit(this.selectedValues);
      } else {
        this.onInputChangeFullValue.emit(data);
      }
    this.onInputChange.emit(val);
  }

  get fullValueSelectedAsync() {
    return this.fullValueSelected?.map((x: any) => this.options.find((o: any) => o.value == x.value) || x)
  }

  nextLevel(item: any) {
    this.cancelClose = true
    item.expanded = !item.expanded
    // this.writeValue(item?.value)
    // this.selectedValues = [item]
    // this.onModelChange(item?.value)
  }
}
